/**
 * Yugen Product Explorer
 *
 * activeIndex is the single source of truth shared by pills, hotspot dots,
 * and detail panels (each panel includes close-up image + copy below the overview).
 */
(function () {
  if (customElements.get('yugen-product-explorer')) return;

  var PANEL_FADE_MS = 400;
  var CALLOUT_GUTTER = 16;
  var CALLOUT_EDGE = 10;

  class YugenProductExplorer extends HTMLElement {
    connectedCallback() {
      this.pills = [...this.querySelectorAll('[data-ype-pill]')];
      this.hotspots = [...this.querySelectorAll('[data-ype-hotspot]')];
      this.panels = [...this.querySelectorAll('[data-ype-panel]')];
      this.panelStack = this.querySelector('[data-ype-panels]');
      this.pillsWrap = this.querySelector('[data-ype-pills]');
      this.navWrap = this.querySelector('.ype__nav');
      this.scrollCta = this.querySelector('[data-ype-scroll-cta]');
      this.hotspotLayer = this.querySelector('[data-ype-hotspots]');
      this.overviewImage = this.querySelector('.ype__img--overview');
      this.calloutLabels = [...this.querySelectorAll('[data-ype-callout-label]')];
      this.calloutPaths = [...this.querySelectorAll('[data-ype-callout-path]')];
      this.calloutUnits = [...this.querySelectorAll('.ype__hotspot-unit--callout')];
      this.hasCallouts = this.calloutLabels.length > 0 && this.hotspotLayer;

      this.total = this.panels.length;
      if (this.total === 0) return;

      this.activeIndex = 0;
      this.panelTransitionTimer = null;
      this.calloutLayoutTimer = null;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onPillClick = this.onPillClick.bind(this);
      this.onHotspotClick = this.onHotspotClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onScrollCtaClick = this.onScrollCtaClick.bind(this);
      this.onPillsScroll = this.onPillsScroll.bind(this);
      this.layoutCallouts = this.layoutCallouts.bind(this);
      this.scheduleCalloutLayout = this.scheduleCalloutLayout.bind(this);

      this.pills.forEach((pill) => pill.addEventListener('click', this.onPillClick));
      this.hotspots.forEach((hotspot) => hotspot.addEventListener('click', this.onHotspotClick));
      if (this.pillsWrap) {
        this.pillsWrap.addEventListener('keydown', this.onKeydown);
        this.pillsWrap.addEventListener('scroll', this.onPillsScroll, { passive: true });
      }
      if (this.scrollCta) this.scrollCta.addEventListener('click', this.onScrollCtaClick);

      if (this.navWrap && this.pillsWrap) {
        this.updateNavScrollState();
        this.navResizeObserver = new ResizeObserver(() => this.updateNavScrollState());
        this.navResizeObserver.observe(this.pillsWrap);
      }

      if (this.hasCallouts) {
        this.calloutResizeObserver = new ResizeObserver(this.scheduleCalloutLayout);
        this.calloutResizeObserver.observe(this.hotspotLayer);
        if (this.overviewImage) {
          this.calloutResizeObserver.observe(this.overviewImage);
          if (!this.overviewImage.complete) {
            this.overviewImage.addEventListener('load', this.scheduleCalloutLayout, { once: true });
          }
        }
        window.addEventListener('resize', this.scheduleCalloutLayout);
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(this.scheduleCalloutLayout);
        }
        this.scheduleCalloutLayout();
      }

      this.selectDetail(0, { instant: true, scrollPill: false });
    }

    disconnectedCallback() {
      this.pills.forEach((pill) => pill.removeEventListener('click', this.onPillClick));
      this.hotspots.forEach((hotspot) => hotspot.removeEventListener('click', this.onHotspotClick));
      if (this.pillsWrap) {
        this.pillsWrap.removeEventListener('keydown', this.onKeydown);
        this.pillsWrap.removeEventListener('scroll', this.onPillsScroll);
      }
      if (this.scrollCta) this.scrollCta.removeEventListener('click', this.onScrollCtaClick);
      if (this.navResizeObserver) this.navResizeObserver.disconnect();
      if (this.calloutResizeObserver) this.calloutResizeObserver.disconnect();
      window.removeEventListener('resize', this.scheduleCalloutLayout);
      if (this.panelTransitionTimer) clearTimeout(this.panelTransitionTimer);
      if (this.calloutLayoutTimer) cancelAnimationFrame(this.calloutLayoutTimer);
    }

    scheduleCalloutLayout() {
      if (!this.hasCallouts) return;
      if (this.calloutLayoutTimer) cancelAnimationFrame(this.calloutLayoutTimer);
      this.calloutLayoutTimer = requestAnimationFrame(() => {
        this.calloutLayoutTimer = requestAnimationFrame(() => this.layoutCallouts());
      });
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    parseObjectPosition(value, fallback) {
      if (!value) return fallback;
      const match = String(value).trim().match(/^([\d.]+)%/);
      if (!match) return fallback;
      return Number(match[1]);
    }

    getHeroFrameRect(img, layerRect) {
      const box = img.getBoundingClientRect();
      return {
        left: box.left - layerRect.left,
        top: box.top - layerRect.top,
        right: box.right - layerRect.left,
        bottom: box.bottom - layerRect.top,
        width: box.width,
        height: box.height,
      };
    }

    /**
     * object-fit: contain paints inside the element box; derive the drawn image
     * rect (honouring object-position) for desktop washi gutters.
     */
    getContainedImageRect(img, layerRect) {
      const box = img.getBoundingClientRect();
      const natW = img.naturalWidth || 0;
      const natH = img.naturalHeight || 0;
      const relLeft = box.left - layerRect.left;
      const relTop = box.top - layerRect.top;

      if (!natW || !natH || box.width < 8 || box.height < 8) {
        return {
          left: relLeft,
          top: relTop,
          right: box.right - layerRect.left,
          bottom: box.bottom - layerRect.top,
          width: box.width,
          height: box.height,
        };
      }

      const scale = Math.min(box.width / natW, box.height / natH);
      const drawW = natW * scale;
      const drawH = natH * scale;
      const style = window.getComputedStyle(img);
      const posParts = String(style.objectPosition || '50% 50%').split(/\s+/);
      const posX = this.parseObjectPosition(posParts[0], 50);
      const posY = this.parseObjectPosition(posParts[1] || posParts[0], 50);
      const left = relLeft + (box.width - drawW) * (posX / 100);
      const top = relTop + (box.height - drawH) * (posY / 100);

      return {
        left,
        top,
        right: left + drawW,
        bottom: top + drawH,
        width: drawW,
        height: drawH,
      };
    }

    layoutCallouts() {
      if (!this.hasCallouts || !this.overviewImage) return;

      const layer = this.hotspotLayer;
      const canvas = layer.querySelector('[data-ype-callouts-canvas]');
      const layerRect = layer.getBoundingClientRect();
      if (layerRect.width < 8 || layerRect.height < 8) return;
      if (!this.overviewImage.naturalWidth) {
        this.overviewImage.addEventListener('load', this.scheduleCalloutLayout, { once: true });
        return;
      }

      const layerW = layerRect.width;
      const layerH = layerRect.height;
      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      const edge = Math.max(10, Math.min(18, layerW * 0.03));

      // Desktop uses contain + washi gutters; mobile uses cover + overlay labels.
      const frame = isDesktop
        ? this.getContainedImageRect(this.overviewImage, layerRect)
        : this.getHeroFrameRect(this.overviewImage, layerRect);

      if (canvas) {
        canvas.setAttribute('viewBox', `0 0 ${layerW} ${layerH}`);
        canvas.setAttribute('width', String(layerW));
        canvas.setAttribute('height', String(layerH));
      }

      const leftItems = [];
      const rightItems = [];

      this.calloutUnits.forEach((unit) => {
        const index = Number(unit.dataset.index);
        if (Number.isNaN(index)) return;

        const label = this.calloutLabels.find((el) => Number(el.dataset.index) === index);
        const path = this.calloutPaths.find((el) => Number(el.dataset.index) === index);
        if (!label || !path) return;

        const xPct = Number(isDesktop ? unit.dataset.xDesktop : unit.dataset.xMobile);
        const yPct = Number(isDesktop ? unit.dataset.yDesktop : unit.dataset.yMobile);
        const safeX = Number.isFinite(xPct) ? xPct : 50;
        const safeY = Number.isFinite(yPct) ? yPct : 50;

        const anchorX = frame.left + (safeX / 100) * frame.width;
        const anchorY = frame.top + (safeY / 100) * frame.height;

        if (isDesktop) {
          // Pin dots to the contained hero.
          unit.style.left = `${anchorX}px`;
          unit.style.top = `${anchorY}px`;
        } else {
          unit.style.left = '';
          unit.style.top = '';
        }

        // Line origin = visual center of the hotspot (after transform / pin).
        const hotspotEl = unit.querySelector('.ype__hotspot') || unit;
        const hotRect = hotspotEl.getBoundingClientRect();
        const hx = hotRect.left + hotRect.width / 2 - layerRect.left;
        const hy = hotRect.top + hotRect.height / 2 - layerRect.top;

        const side = safeX > 50 ? 'right' : 'left';
        const bendPx = (Number(unit.dataset.calloutBend) || 0) * (isDesktop ? 0.45 : 0.35);
        const item = { index, label, path, hx, hy, side, bendPx };
        if (side === 'left') leftItems.push(item);
        else rightItems.push(item);
      });

      if (isDesktop) {
        this.placeCalloutsInWashi(leftItems, rightItems, frame, layerW, layerH, edge);
      } else {
        this.placeCalloutsOverlay(leftItems, rightItems, layerW, layerH, edge);
      }
    }

    placeCalloutsInWashi(leftItems, rightItems, frame, layerW, layerH, edge) {
      const gutter = Math.max(14, Math.min(28, (layerW - frame.width) * 0.08));

      const placeSide = (items, side) => {
        items.sort((a, b) => a.hy - b.hy);
        const count = items.length;

        items.forEach((item, i) => {
          const { label, path, hx, hy, bendPx } = item;
          const neighborSpread = count > 1 ? (i - (count - 1) / 2) * 16 : 0;
          let ly = this.clamp(hy + bendPx + neighborSpread, frame.top + 12, frame.bottom - 12);

          let midX;
          let lx;
          if (side === 'left') {
            midX = frame.left - Math.min(gutter * 0.3, 10);
            lx = frame.left - gutter;
          } else {
            midX = frame.right + Math.min(gutter * 0.3, 10);
            lx = frame.right + gutter;
          }

          label.dataset.side = side;
          label.classList.add('is-ready');
          label.style.left = `${lx}px`;
          label.style.top = `${ly}px`;

          const labelW = Math.max(label.offsetWidth || 0, 64);
          const labelH = Math.max(label.offsetHeight || 0, 16);

          if (side === 'left') {
            const minLx = edge + labelW;
            const maxLx = Math.max(minLx, frame.left - 8);
            lx = this.clamp(lx, minLx, maxLx);
          } else {
            const maxLx = layerW - edge - labelW;
            const minLx = Math.min(maxLx, frame.right + 8);
            lx = this.clamp(lx, minLx, maxLx);
          }

          ly = this.clamp(ly, edge + labelH / 2, layerH - edge - labelH / 2);
          label.style.left = `${lx}px`;
          label.style.top = `${ly}px`;

          // Orthogonal only: out horizontally, then vertical, then into the label.
          path.setAttribute('d', this.orthogonalCalloutPath(hx, hy, midX, lx, ly));
        });
      };

      placeSide(leftItems, 'left');
      placeSide(rightItems, 'right');
    }

    placeCalloutsOverlay(leftItems, rightItems, layerW, layerH, edge) {
      const placeSide = (items, side) => {
        items.sort((a, b) => a.hy - b.hy);
        const count = items.length;

        items.forEach((item, i) => {
          const { label, path, hx, hy, bendPx } = item;
          const neighborSpread = count > 1 ? (i - (count - 1) / 2) * 18 : 0;
          let ly = this.clamp(hy + bendPx + neighborSpread, edge + 14, layerH - edge - 14);

          let lx = side === 'left' ? edge + 6 : layerW - edge - 6;
          label.dataset.side = side;
          label.classList.add('is-ready');
          label.style.left = `${lx}px`;
          label.style.top = `${ly}px`;

          const labelW = Math.max(label.offsetWidth || 0, 64);
          const labelH = Math.max(label.offsetHeight || 0, 16);

          if (side === 'left') {
            lx = this.clamp(
              Math.max(edge + labelW, hx * 0.42),
              edge + labelW,
              Math.max(edge + labelW, hx - 18)
            );
          } else {
            lx = this.clamp(
              Math.min(layerW - edge - labelW, hx + (layerW - hx) * 0.55),
              Math.min(layerW - edge - labelW, hx + 18),
              layerW - edge - labelW
            );
          }

          ly = this.clamp(ly, edge + labelH / 2, layerH - edge - labelH / 2);
          label.style.left = `${lx}px`;
          label.style.top = `${ly}px`;

          const elbowX = side === 'left' ? Math.max(hx - 12, lx + 8) : Math.min(hx + 12, lx - 8);

          path.setAttribute('d', this.orthogonalCalloutPath(hx, hy, elbowX, lx, ly));
        });
      };

      placeSide(leftItems, 'left');
      placeSide(rightItems, 'right');
    }

    /** Horizontal → vertical → horizontal (right angles only). */
    orthogonalCalloutPath(hx, hy, elbowX, lx, ly) {
      const x0 = hx.toFixed(1);
      const y0 = hy.toFixed(1);
      const x1 = elbowX.toFixed(1);
      const y1 = ly.toFixed(1);
      const x2 = lx.toFixed(1);

      if (Math.abs(hy - ly) < 0.5) {
        return `M ${x0} ${y0} L ${x2} ${y0}`;
      }
      if (Math.abs(hx - elbowX) < 0.5) {
        return `M ${x0} ${y0} L ${x0} ${y1} L ${x2} ${y1}`;
      }
      return `M ${x0} ${y0} L ${x1} ${y0} L ${x1} ${y1} L ${x2} ${y1}`;
    }

    onPillsScroll() {
      this.updateNavScrollState();
    }

    updateNavScrollState() {
      const list = this.pillsWrap;
      const nav = this.navWrap;
      if (!list || !nav) return;

      const scrollable = list.scrollWidth > list.clientWidth + 1;
      nav.classList.toggle('is-scrollable', scrollable);
      nav.classList.toggle('is-at-start', list.scrollLeft <= 1);
      nav.classList.toggle('is-at-end', list.scrollLeft + list.clientWidth >= list.scrollWidth - 1);
    }

    onScrollCtaClick(event) {
      const href = this.scrollCta.getAttribute('href') || '';
      if (href.charAt(0) !== '#' || href.length < 2) return;

      const target = document.getElementById(href.slice(1));
      if (!target) return;

      event.preventDefault();

      const reduce = this.reduceMotion;
      target.scrollIntoView({
        behavior: reduce ? 'auto' : 'smooth',
        block: 'start',
      });

      if (history.replaceState) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }

      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }

    onPillClick(event) {
      const index = Number(event.currentTarget.dataset.index);
      if (Number.isNaN(index)) return;
      this.selectDetail(index, { scrollPill: true });
    }

    onHotspotClick(event) {
      const index = Number(event.currentTarget.dataset.index);
      if (Number.isNaN(index)) return;
      this.selectDetail(index, { scrollPill: true });
    }

    onKeydown(event) {
      if (!this.pills.length) return;

      let next = this.activeIndex;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (this.activeIndex + 1) % this.total;
          event.preventDefault();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (this.activeIndex - 1 + this.total) % this.total;
          event.preventDefault();
          break;
        case 'Home':
          next = 0;
          event.preventDefault();
          break;
        case 'End':
          next = this.total - 1;
          event.preventDefault();
          break;
        default:
          return;
      }

      this.selectDetail(next, { scrollPill: true });
      this.pills[next].focus();
    }

    selectDetail(index, options = {}) {
      if (index < 0 || index >= this.total) return;
      if (index === this.activeIndex && !options.instant) return;

      const previousIndex = this.activeIndex;
      this.activeIndex = index;
      this.syncPills(index, options);
      this.syncHotspots(index);
      this.syncCallouts(index);
      this.syncPanels(index, previousIndex, options);
    }

    scrollPillIntoNav(pill) {
      const wrap = this.pillsWrap;
      if (!pill || !wrap) return;

      const wrapRect = wrap.getBoundingClientRect();
      const pillRect = pill.getBoundingClientRect();
      const edgeInset = 12;
      let nextScrollLeft = wrap.scrollLeft;

      if (pillRect.left < wrapRect.left + edgeInset) {
        nextScrollLeft -= wrapRect.left + edgeInset - pillRect.left;
      } else if (pillRect.right > wrapRect.right - edgeInset) {
        nextScrollLeft += pillRect.right - (wrapRect.right - edgeInset);
      } else {
        return;
      }

      wrap.scrollTo({
        left: nextScrollLeft,
        behavior: this.reduceMotion ? 'auto' : 'smooth',
      });
    }

    syncPills(index, options = {}) {
      const scrollPill = options.scrollPill === true;

      this.pills.forEach((pill, i) => {
        const selected = i === index;
        pill.setAttribute('aria-selected', selected ? 'true' : 'false');
        pill.tabIndex = selected ? 0 : -1;

        if (selected && scrollPill) {
          this.scrollPillIntoNav(pill);
          this.updateNavScrollState();
        }
      });
    }

    syncHotspots(index) {
      this.hotspots.forEach((hotspot, i) => {
        const selected = i === index;
        hotspot.classList.toggle('is-active', selected);
        hotspot.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    syncCallouts(index) {
      if (!this.hasCallouts) return;
      this.calloutLabels.forEach((label) => {
        label.classList.toggle('is-active', Number(label.dataset.index) === index);
      });
      this.calloutPaths.forEach((path) => {
        path.classList.toggle('is-active', Number(path.dataset.index) === index);
      });
    }

    setPanelInert(panel, inert) {
      if ('inert' in panel) panel.inert = inert;
    }

    finishPanelTransition(index) {
      this.panels.forEach((panel, i) => {
        panel.classList.remove('is-leaving');
        const active = i === index;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        this.setPanelInert(panel, !active);
      });

      if (this.panelStack) this.panelStack.style.minHeight = '';
      this.panelTransitionTimer = null;
    }

    syncPanels(index, previousIndex, options = {}) {
      const instant = options.instant || this.reduceMotion;
      const nextPanel = this.panels[index];
      const prevPanel =
        previousIndex !== index && previousIndex >= 0 ? this.panels[previousIndex] : null;

      if (this.panelTransitionTimer) {
        clearTimeout(this.panelTransitionTimer);
        this.panelTransitionTimer = null;
      }

      this.panels.forEach((panel) => panel.classList.remove('is-leaving'));

      if (instant || !prevPanel) {
        this.finishPanelTransition(index);
        return;
      }

      if (this.panelStack) {
        const prevHeight = prevPanel.offsetHeight;
        const wasHidden = nextPanel.hidden;
        nextPanel.hidden = false;
        nextPanel.classList.add('is-active');
        const nextHeight = nextPanel.offsetHeight;
        nextPanel.classList.remove('is-active');
        if (wasHidden) nextPanel.hidden = true;
        this.panelStack.style.minHeight = Math.max(prevHeight, nextHeight, 0) + 'px';
      }

      nextPanel.hidden = false;
      nextPanel.setAttribute('aria-hidden', 'false');
      this.setPanelInert(nextPanel, false);
      nextPanel.classList.remove('is-active');

      prevPanel.classList.remove('is-active');
      prevPanel.classList.add('is-leaving');
      prevPanel.setAttribute('aria-hidden', 'true');
      this.setPanelInert(prevPanel, true);

      this.panels.forEach((panel, i) => {
        if (i === index || panel === prevPanel) return;
        panel.hidden = true;
        panel.classList.remove('is-active', 'is-leaving');
        panel.setAttribute('aria-hidden', 'true');
        this.setPanelInert(panel, true);
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          nextPanel.classList.add('is-active');
        });
      });

      this.panelTransitionTimer = setTimeout(() => {
        this.finishPanelTransition(index);
      }, PANEL_FADE_MS);
    }
  }

  customElements.define('yugen-product-explorer', YugenProductExplorer);
})();
